import { getNewId } from '@/utils/id'
import api from '../api/api'

export default class {
  constructor(baseUrl) {
    this.state = {
      baseUrl: baseUrl,
      items: [],
      dirtyItems: [],
      options: null,
      count: null,
      loading: false,
      dialog: false,
      deleteDialog: false,
      currentItem: null,
      writeLoading: false
    }
    this.mutations = {
      SET_ITEMS(state, items) {
        // Dirty items and current item need to be reset to make sure no old data is available
        state.dirtyItems = []
        state.currentItem = null
        state.items = items
      },
      ADD_ITEMS(state, items) {
        if (!state.items) {
          state.items = []
        } else if (!items.length) {
          return null
        }
        state.items.push(...items)
      },
      SET_CURRENT_ITEM(state, item) {
        state.currentItem = item
      },
      SET_OPTIONS(state, options) {
        state.options = options
      },
      ADD_ITEM(state, item) {
        state.items.unshift(item)
      },
      ADD_ITEM_ON_INDEX(state, { item, index }) {
        state.items.splice(index, 0, item)
      },
      REPLACE_ITEM(state, item) {
        let index = state.items.findIndex((element) => element.id === item.id)
        state.items[index] = item
      },
      REPLACE_ITEMS(state, { newObjs, createdObjs }) {
        newObjs.forEach((obj, index) => {
          let itemIndex = state.items.findIndex((item) => item.id === obj.id)
          state.items[itemIndex] = createdObjs[index]
        })
      },
      ADD_DIRTY_ITEM(state, item) {
        if (!state.dirtyItems) {
          state.dirtyItems = []
        }
        let found = state.dirtyItems.find((dirtyItem) => item.id === dirtyItem.id)
        if (!found) {
          state.dirtyItems.push(item)
        }
      },
      EMPTY_DIRTY_ITEM(state) {
        state.dirtyItems = []
      },
      ADD_COUNT(state, count) {
        state.count += count
      },
      EMPTY_COUNT(state, count) {
        state.count = count
      },
      TOGGLE_LOADING(state) {
        state.loading = !state.loading
      },
      TOGGLE_DIALOG(state) {
        state.dialog = !state.dialog
      },
      TOGGLE_DELETE_DIALOG(state) {
        state.deleteDialog = !state.deleteDialog
      },
      TOGGLE_WRITE_LOADING(state, bool = null) {
        if (bool !== null) {
          state.writeLoading = bool
        } else {
          state.writeLoading = !state.writeLoading
        }
      },
      SET_PARAMETER(state, { value, item, parameter }) {
        if (isNaN(parseFloat(value))) {
          item[parameter] = value
        } else {
          item[parameter] = parseFloat(value)
        }
      },
      SET_NESTED_PARAMETER(state, { value, item, parameter }) {
        var arr = parameter.split('.')
        for (var i = 0; i < arr.length - 1; i++) {
          if (item) item = item[arr[i]]
        }
        if (!isNaN(parseFloat(value))) {
          value = parseFloat(value)
        }
        item[arr[arr.length - 1]] = value
      },
      DELETE_ITEM(state, id) {
        state.count -= 1
        let refIndex = state.items.findIndex((item) => item.id === id)
        state.items.splice(refIndex, 1)
        let refDirtyIndex = state.dirtyItems.findIndex((item) => item.id === id)
        refDirtyIndex !== -1 ? state.dirtyItems.splice(refDirtyIndex, 1) : null
      }
    }
    this.actions = {
      async fetchOptions(context) {
        if (!context.state.options) {
          let response = await api.options(context.state.baseUrl)
          context.commit('SET_OPTIONS', response.data.actions.POST)
        }
      },
      async deleteItem(context, id) {
        if (id > 0) {
          let url = context.state.baseUrl + `${id}/`
          await api.delete(url)
        }
        context.commit('DELETE_ITEM', id)
      },
      async fetchItem(context, id) {
        context.commit('TOGGLE_LOADING')
        let url = context.state.baseUrl + `${id}/`
        let response = await api.get(url)
        context.commit('SET_CURRENT_ITEM', response.data)
        context.commit('TOGGLE_LOADING')
      },
      async fetchItemAndReplace(context, id) {
        context.commit('TOGGLE_LOADING')
        let url = context.state.baseUrl + `${id}/`
        let response = await api.get(url)
        context.commit('REPLACE_ITEM', response.data)
        context.commit('TOGGLE_LOADING')
      },
      async fetchItems(context, id) {
        context.commit('TOGGLE_LOADING')
        let url = context.state.baseUrl + `?search=${id}`
        let response = await api.get(url)
        context.commit('SET_ITEMS', response.data)
        context.commit('ADD_COUNT', response.data.length)
        context.commit('TOGGLE_LOADING')
      },
      async fetchItemsAdd(context, id) {
        let url = context.state.baseUrl + `?search=${id}`
        let response = await api.get(url)
        context.commit('ADD_ITEMS', response.data)
      },
      async update(context) {
        let url = baseUrl + 'bulk_update/'
        let updatedObj = context.state.dirtyItems
          ? context.state.dirtyItems.filter((dirtyItem) => dirtyItem.id > 0)
          : []
        if (updatedObj.length) {
          await api.patch(url, updatedObj)
        }
      },
      add(context, { id, field }) {
        let newId = getNewId(context.state.items, 'id')
        let item = {}
        if (context.state.options) {
          Object.keys(context.state.options).forEach((optionKey) =>
            context.state.options[optionKey].default !== null
              ? (item[optionKey] = context.state.options[optionKey].default)
              : null
          )
        }
        item['id'] = parseInt(newId)
        item[field] = id
        context.commit('ADD_ITEM', item)
      },
      async createItem(context, payload) {
        context.commit('TOGGLE_WRITE_LOADING')
        try {
          let response = await api.post(baseUrl, payload)
          context.commit('ADD_ITEM', response.data)
        } catch (error) {
          console.log(error)
        }
        context.commit('TOGGLE_WRITE_LOADING')
      },
      async updateItem(context, payload) {
        context.commit('TOGGLE_WRITE_LOADING')
        let url = baseUrl + payload.id + '/'
        await api.patch(url, payload)
        context.commit('TOGGLE_WRITE_LOADING')
      },
      async create(context) {
        context.commit('TOGGLE_WRITE_LOADING')
        let newObjs = context.state.items.filter((item) => item.id <= 0)
        if (newObjs.length) {
          let response = await api.post(baseUrl, newObjs)
          let createdObjs = response.data
          context.commit('REPLACE_ITEMS', { newObjs, createdObjs })
        }
        context.commit('TOGGLE_WRITE_LOADING')
      },
      emptyDirtyItems(context) {
        context.commit('EMPTY_DIRTY_ITEM')
      },
      toggleDialog(context) {
        context.commit('TOGGLE_DIALOG')
      },
      setParameter(context, payload) {
        context.commit('SET_PARAMETER', payload)
        context.commit('ADD_DIRTY_ITEM', payload.item)
      },
      setCurrentItem(context, payload) {
        context.commit('SET_CURRENT_ITEM', payload)
      },
      setNestedParameter(context, payload) {
        context.commit('SET_NESTED_PARAMETER', payload)
        context.commit('ADD_DIRTY_ITEM', payload.item)
      },
      setDirtyOnArray(context, items) {
        items.forEach((item) => context.commit('ADD_DIRTY_ITEM', item))
      },
      async deleteFromState(context, item) {
        context.commit('DELETE_ITEM', item.id)
      },
      toggleWriteLoading(context, bool = null) {
        context.commit('TOGGLE_WRITE_LOADING', bool)
      },
      toggleLoading(context, bool = null) {
        context.commit('TOGGLE_LOADING', bool)
      },
      toggleDeleteDialog(context) {
        context.commit('TOGGLE_DELETE_DIALOG')
      }
    }
  }
}
